import { Vec2 } from '../engine';
import { ARENA, GAME_CONFIG } from './constants';
import { FighterState, FighterStats } from './state';

type AttackType = 'punch' | 'kick';

const ATTACKS: Record<AttackType, { duration: number; cooldown: number; force: number; multiplier: number; knockback: number }> = {
  punch: { duration: 0.25, cooldown: 0.4, force: 1.2, multiplier: 1, knockback: 65 },
  kick: { duration: 0.35, cooldown: 0.6, force: 1.6, multiplier: 1.45, knockback: 85 },
};

export interface AttackResult {
  hitFrame: number;
  damage: number;
  knockback: number;
  type: AttackType;
}

export class Fighter {
  position: Vec2;
  velocity = new Vec2();
  readonly width = 24;
  readonly height = 30;
  facing: 1 | -1 = 1;
  stats: FighterStats;
  state: FighterState;
  private attackTimer = 0;
  private attackHit = false;
  private attackSlot?: AttackType;
  private pendingAttack?: AttackResult;
  private invulnerability = 0;

  constructor(position: Vec2, stats: FighterStats) {
    this.position = position;
    this.stats = stats;
    this.state = {
      health: stats.maxHealth,
      attacking: false,
      attackCooldown: 0,
    };
  }

  reset(position: Vec2, stats: FighterStats): void {
    this.position = position.clone();
    this.stats = stats;
    this.state.health = stats.maxHealth;
    this.state.attacking = false;
    this.state.attackCooldown = 0;
    this.attackTimer = 0;
    this.attackHit = false;
    this.attackSlot = undefined;
    this.pendingAttack = undefined;
    this.invulnerability = 0;
    this.velocity.set(0, 0);
  }

  update(dt: number): void {
    if (this.state.attackCooldown > 0) {
      this.state.attackCooldown = Math.max(0, this.state.attackCooldown - dt);
    }
    if (this.state.attacking) {
      this.attackTimer -= dt;
      if (this.attackTimer <= 0) {
        this.state.attacking = false;
        this.attackHit = false;
        this.attackSlot = undefined;
        this.pendingAttack = undefined;
      }
    }

    if (this.invulnerability > 0) {
      this.invulnerability = Math.max(0, this.invulnerability - dt);
    }

    this.applyPhysics(dt);
  }

  private applyPhysics(dt: number): void {
    const gravity = 820;
    this.velocity.y += gravity * dt;
    this.position.add(this.velocity.clone().scale(dt));
    this.velocity.x *= 0.9;
    this.velocity.y = Math.min(this.velocity.y, 520);

    const minX = 20;
    const maxX = ARENA.width - 20 - this.width;
    if (this.position.x < minX) {
      this.position.x = minX;
      this.velocity.x = Math.max(90, Math.abs(this.velocity.x) * 0.95);
      this.facing = 1;
    }
    if (this.position.x > maxX) {
      this.position.x = maxX;
      this.velocity.x = -Math.max(90, Math.abs(this.velocity.x) * 0.95);
      this.facing = -1;
    }

    const groundY = ARENA.groundY - this.height;
    if (this.position.y > groundY) {
      this.position.y = groundY;
      this.velocity.y = 0;
    }
  }

  move(direction: number, dt: number): void {
    const acceleration = this.stats.speed * 6 * dt;
    if (direction !== 0) {
      const target = direction * this.stats.speed;
      if (target > this.velocity.x) {
        this.velocity.x = Math.min(target, this.velocity.x + acceleration);
      } else {
        this.velocity.x = Math.max(target, this.velocity.x - acceleration);
      }
      this.facing = direction > 0 ? 1 : -1;
    } else {
      const decay = Math.min(Math.abs(this.velocity.x), acceleration);
      this.velocity.x -= Math.sign(this.velocity.x) * decay;
    }
    this.velocity.x = Math.max(-this.stats.speed, Math.min(this.stats.speed, this.velocity.x));
  }

  jump(): void {
    if (this.position.y >= ARENA.groundY - this.height - 0.5) {
      this.velocity.y = -280;
    }
  }

  tryAttack(type: AttackType): AttackResult | null {
    if (this.state.attacking || this.state.attackCooldown > 0) {
      return null;
    }
    const config = ATTACKS[type];
    this.state.attacking = true;
    this.state.attackCooldown = config.cooldown;
    this.attackTimer = config.duration;
    this.attackHit = false;
    this.attackSlot = type;
    const damage = this.stats.strength * config.multiplier;
    this.pendingAttack = {
      hitFrame: 0,
      damage,
      knockback: config.knockback,
      type,
    };
    return this.pendingAttack;
  }

  resolveHit(): void {
    this.attackHit = true;
    this.pendingAttack = undefined;
  }

  canDealDamage(): boolean {
    return this.state.attacking && !this.attackHit && !!this.pendingAttack;
  }

  currentAttack(): AttackResult | undefined {
    return this.pendingAttack;
  }

  attackType(): AttackType | undefined {
    return this.attackSlot;
  }

  isAttacking(): boolean {
    return this.state.attacking;
  }

  attackProgress(): number {
    if (!this.state.attacking || !this.attackSlot) {
      return 0;
    }
    const config = ATTACKS[this.attackSlot];
    return 1 - this.attackTimer / config.duration;
  }

  horizontalSpeed(): number {
    return Math.abs(this.velocity.x);
  }

  takeDamage(amount: number, knockback: number, sourceDirection: number): void {
    if (this.invulnerability > 0) {
      return;
    }
    this.state.health = Math.max(0, this.state.health - amount);
    this.velocity.x += knockback * sourceDirection;
    this.invulnerability = 0.2;
  }

  isAlive(): boolean {
    return this.state.health > 0;
  }

  getBounds(): { left: number; right: number; top: number; bottom: number } {
    return {
      left: this.position.x,
      right: this.position.x + this.width,
      top: this.position.y,
      bottom: this.position.y + this.height,
    };
  }

  faceTarget(targetX: number): void {
    this.facing = targetX > this.position.x ? 1 : -1;
  }
}
